import { Children } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import CloseRounded from "@mui/icons-material/CloseRounded";
import PropTypes from "prop-types";

import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { PanelIconButton } from "../sidebar/shared/panelChrome";
import { formSx, px } from "../../theme/formStyles";
import { FormFieldTextProvider } from "./FormPrimitives";

/**
 * The small edit modal — ONE or TWO inputs and nothing else: change a date,
 * rename something, set an amount, pick a status.
 *
 * It is `ConfirmationDialog`'s sibling (FORM_STYLE_GUIDE.md §12c) with a
 * control where the sentence was, so it makes the same two calls: the title is
 * 18/600 and the header carries **no hairline** — there are no regions for a
 * rule to separate. The footer keeps its rule, which separates the input from
 * the actions. Sides are 24px and the paper caps at 460: a single field inside
 * the full 880px form modal reads as a page with one field on it.
 *
 * **Enter saves.** The body is a real `<form>`, so a date or text field submits
 * on Enter and the primary button wears a ⏎ cap saying so. Pass
 * `enterHint={false}` where the single control swallows Enter (a multiline
 * note, an autocomplete).
 *
 * **With one input the field's label is hidden** and the TITLE names it: a
 * modal headed "Scheduled closing date" over a field captioned "Scheduled
 * closing date" says it twice, and the caption is the copy that loses. So
 * title the modal with whatever the field's label would have said. The label
 * stays in the DOM, visually clipped, so screen readers keep it. With two
 * fields both labels show — there the title can't name them both. Override
 * either way with `hideFieldLabel`.
 *
 * Use the FULL modal shell instead (`formSx.paper` / `head` / `shellBody`,
 * §12a) the moment the form grows sections, a record line above the title, a
 * "current value" echo, an audit-note field, a document picker or an upload.
 * This one deliberately has no room for any of that.
 *
 * Props:
 * - open / onClose — visibility; `onClose` is the Cancel + X + backdrop path,
 *     and is suppressed while `saving`.
 * - title: node — what is being changed ("Edit scheduled closing date").
 * - description: node (optional) — ONE quiet line under the title. Use it for
 *     a constraint the user needs before touching the control; per-field
 *     guidance belongs in the field's own `helperText`.
 * - children — the field(s), built from FormPrimitives (`FormDateField`,
 *     `FormTextField`, `FormSelectField`, …). Stacked 20px apart.
 * - onSave: () => void — fired by the primary button AND by Enter.
 * - saveText / cancelText: string — button labels.
 * - canSave: boolean — false disables the primary button. The footer never
 *     lists what is missing (§3); the button just stays disabled.
 * - saving: boolean — spinner on the primary, everything else disabled.
 * - status: node (optional) — quiet text in the footer's left slot.
 * - destructive: boolean — paints the primary as the error action.
 * - enterHint: boolean — show the ⏎ cap (default true).
 * - hideFieldLabel: boolean (optional) — force the field label hidden or
 *     shown. The default is "hidden when there is exactly one child", which is
 *     what the child COUNT can tell us; set it explicitly when one child holds
 *     two fields, or when a lone control is not named by the title.
 * - formId: string (optional) — the body brings its OWN `<form id=…>` (a
 *     react-final-form field set, say). Save then submits that form by id
 *     instead of this shell, which renders no form of its own. `onSave` is
 *     unused in that mode; the inner form's submit handler saves.
 * - width: number — paper cap in px, when 460 is genuinely wrong.
 * - fieldText: "standard" | "field" — the label / helper type of the
 *     FormPrimitives fields inside. "field" is the 2026-10-08 standard (12.5px
 *     #111827 label, 11.5px #64748B helper) — pass it on every NEW modal form
 *     (CLAUDE.md "Form field labels & helpers"). Default "standard" keeps the
 *     existing modals as they were.
 * - bodySx: object (optional) — merged onto the field area, e.g. top padding
 *     when a taller form wants air between the title and its first field (the
 *     body has none by default — the header's bottom padding is the gap).
 *
 * Example:
 *   <QuickEditDialog
 *     open={editing}
 *     title="Edit scheduled closing date"
 *     onClose={() => setEditing(false)}
 *     onSave={save}
 *     canSave={!!date}
 *     saving={busy}
 *   >
 *     <FormDateField
 *       label="Scheduled closing date"
 *       value={date}
 *       onChange={setDate}
 *       helperText="Borrower and title are notified of the new date."
 *     />
 *   </QuickEditDialog>
 */
const QuickEditDialog = ({
  open,
  title,
  description,
  children,
  onClose,
  onSave,
  saveText = "Save",
  cancelText = "Cancel",
  canSave = true,
  saving = false,
  status,
  destructive = false,
  enterHint = true,
  hideClose = false,
  hideFieldLabel,
  formId,
  width,
  bodySx,
  fieldText = "standard",
}) => {
  // One field → its label is the title's job (see the header note above).
  const hideLabel =
    typeof hideFieldLabel === "boolean"
      ? hideFieldLabel
      : Children.count(children) === 1;
  const handleSubmit = (e) => {
    e.preventDefault();
    if (saving || !canSave) return;
    onSave?.();
  };

  // With `formId` the BODY carries the <form> (a react-final-form field set,
  // say) and Save reaches it by id — nesting one form inside another is
  // invalid HTML and the inner one stops submitting. Without it, this shell
  // is the form and `onSave` is the submit handler.
  const shell = formId
    ? { component: "div" }
    : { component: "form", onSubmit: handleSubmit, noValidate: true };

  // Header and footer are PINNED and only the field area scrolls — the same
  // division as every other shell here. The paper itself must therefore never
  // scroll: MUI's dialog paper is `overflow-y: auto`, so any stray overflow
  // (a grid gutter a few px too wide) would otherwise let the title scroll
  // out of the modal, which is what it looks like when it happens.
  const shellSx = {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    maxHeight: "100%",
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      onClose={saving ? undefined : onClose}
      aria-labelledby="quick-edit-dialog-title"
      PaperProps={{
        sx: {
          ...formSx.quickPaper,
          overflow: "hidden",
          ...(width ? { maxWidth: width } : null),
        },
      }}
    >
      <Box {...shell} sx={shellSx}>
        {/* header — title + optional line, close X; no hairline */}
        <Box sx={{ ...formSx.quickHead, flexShrink: 0 }}>
          <Flx jb g={2} sx={{ alignItems: "flex-start" }}>
            <Box sx={{ minWidth: 0 }} id="quick-edit-dialog-title">
              <Txt sx={formSx.quickTitle}>{title}</Txt>
              {description ? (
                <Txt sx={formSx.titleHelper}>{description}</Txt>
              ) : null}
            </Box>
            {!hideClose && (
              // Nudged into the header's padding so the 30px hit target lines
              // up with the title's cap height and the paper's right edge.
              <PanelIconButton
                icon={CloseRounded}
                title="Close"
                onClick={onClose}
                disabled={saving}
                sx={{ mt: px(-4), mr: px(-6), flexShrink: 0 }}
              />
            )}
          </Flx>
        </Box>

        <Box sx={{ ...formSx.quickBody, ...bodySx }}>
          <Box
            sx={{
              ...formSx.quickFields,
              ...(hideLabel ? formSx.quickHiddenLabel : null),
            }}
          >
            <FormFieldTextProvider value={fieldText}>
              {children}
            </FormFieldTextProvider>
          </Box>
        </Box>

        {/* footer — hairline on top, status left, actions right */}
        <Box sx={formSx.quickFoot}>
          <Box sx={{ ...formSx.footStatus, minWidth: 0 }}>{status}</Box>
          <Box sx={formSx.buttonRow}>
            <Button
              type="button"
              variant="outlined"
              color="gray"
              onClick={onClose}
              disabled={saving}
              sx={formSx.button}
            >
              {cancelText}
            </Button>
            <Button
              type="submit"
              {...(formId ? { form: formId } : null)}
              variant="contained"
              loading={saving}
              disabled={!canSave}
              {...(destructive ? { color: "error" } : {})}
              sx={formSx.button}
            >
              {saveText}
              {enterHint ? (
                <Box component="span" sx={formSx.quickEnterKey} aria-hidden="true">
                  ⏎
                </Box>
              ) : null}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

QuickEditDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.node,
  description: PropTypes.node,
  children: PropTypes.node,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  saveText: PropTypes.string,
  cancelText: PropTypes.string,
  canSave: PropTypes.bool,
  saving: PropTypes.bool,
  status: PropTypes.node,
  destructive: PropTypes.bool,
  enterHint: PropTypes.bool,
  hideClose: PropTypes.bool,
  hideFieldLabel: PropTypes.bool,
  formId: PropTypes.string,
  width: PropTypes.number,
  bodySx: PropTypes.object,
  fieldText: PropTypes.oneOf(["standard", "field"]),
};

export default QuickEditDialog;
